import { money } from './format.js';
/**
 * Подписи признаков «Кто покупает» и их значений (спека §5.4). Значения —
 * дословный перенос `aura-admin/src/autoAdsManagement/analytics/utils/
 * paybackMarkerLabels.ts`: сырой ключ разбивки (`'True'`/`'False'`/`'None'`,
 * любой регистр) на экран не попадает никогда.
 */
export const MARKER_KEY_LABELS = {
    trial_price_tier: 'Цена триала ($)',
    trial_choice: 'Экран выбора',
    age_bucket: 'Возраст',
    gender: 'Пол',
    region: 'Регион',
    card_funding: 'Тип карты',
    first_attempt_failed: 'Отказ 1-й попытки',
    upsell24_any: 'Докупка за сутки',
    cancel24: 'Отмена за сутки',
    first_cycle_paid_14: 'Первое списание (14 дн)',
    country: 'Страна',
    card_brand: 'Бренд карты',
    three_ds: '3-D Secure',
};
export function markerKeyLabel(key) {
    return MARKER_KEY_LABELS[key] ?? 'Признак';
}
/** Основные признаки (видны сразу) и дополнительные (длинные хвосты). */
export const PRIMARY_MARKER_KEYS = [
    'trial_price_tier',
    'trial_choice',
    'age_bucket',
    'gender',
    'region',
    'card_funding',
    'first_attempt_failed',
    'upsell24_any',
    'cancel24',
    'first_cycle_paid_14',
];
export const EXTRA_MARKER_KEYS = [
    'country',
    'card_brand',
    'three_ds',
];
export const MARKER_GROUPS = [
    { label: 'Основные', keys: PRIMARY_MARKER_KEYS },
    { label: 'Дополнительные', keys: EXTRA_MARKER_KEYS },
];
/** Когда признак становится известен: `t0` сразу, `t24` через сутки, `d14` на 14-й день. */
const MARKER_MOMENT = {
    trial_price_tier: 't0',
    trial_choice: 't0',
    age_bucket: 't0',
    gender: 't0',
    country: 't0',
    region: 't0',
    card_funding: 't0',
    card_brand: 't0',
    first_attempt_failed: 't0',
    upsell24_any: 't24',
    cancel24: 't24',
    first_cycle_paid_14: 'd14',
    three_ds: 't0',
};
const KNOWN_AT = {
    t0: 'известен сразу',
    t24: 'известен через сутки',
    d14: 'известен на 14-й день',
};
export function markerKnownAt(key) {
    return KNOWN_AT[MARKER_MOMENT[key] ?? 't0'];
}
// --- значения (дословно из aura-admin) ---------------------------------------
const NULL_TOKENS = new Set(['none', 'null', 'unknown', 'n/a', 'na', '']);
const TRUE_TOKENS = new Set(['true']);
const FALSE_TOKENS = new Set(['false']);
const FIRST_CYCLE_PAID_14_MARKER = 'first_cycle_paid_14';
const NOT_YET_TOKEN = 'not_yet';
const NOT_YET_LABEL = 'ещё не наступило';
const GENDER_LABELS = { female: 'Женский', male: 'Мужской' };
const CARD_FUNDING_LABELS = {
    credit: 'Кредитная',
    debit: 'Дебетовая',
    prepaid: 'Предоплаченная',
};
const TRIAL_CHOICE_LABELS = {
    choice: 'с выбором',
    no_choice: 'без выбора',
};
/** Регионы auto-ads (`region`) — все значения из реальных ответов (фикстуры). */
export const REGION_LABELS = {
    us: 'США',
    ca: 'Канада',
    uk_ie: 'Великобритания и Ирландия',
    anz: 'Австралия и Новая Зеландия',
    eu_west: 'Западная Европа',
    eu_north: 'Северная Европа',
    eu_east: 'Восточная Европа',
    latam_es: 'Латинская Америка (испаноязычная)',
    latam_pt: 'Латинская Америка (португалоязычная)',
    mena: 'Ближний Восток и Северная Африка',
    africa: 'Африка',
    asia_east: 'Восточная Азия',
    asia_se: 'Юго-Восточная Азия',
    asia_south: 'Южная Азия',
    other: 'Другие страны',
};
const CARD_BRAND_LABELS = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    american_express: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    diners_club: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
    cartes_bancaires: 'Cartes Bancaires',
    eftpos_au: 'eftpos',
    interac: 'Interac',
    link: 'Link',
};
const MARKER_VALUE_DICTS = {
    gender: GENDER_LABELS,
    card_funding: CARD_FUNDING_LABELS,
    trial_choice: TRIAL_CHOICE_LABELS,
    region: REGION_LABELS,
    card_brand: CARD_BRAND_LABELS,
};
let countryNames;
/**
 * Привычные короткие названия поверх ICU (там «Соединенные Штаты» без «ё»,
 * «Республика Корея», длинные официальные формы).
 */
const COUNTRY_OVERRIDES = {
    US: 'США',
    GB: 'Великобритания',
    AE: 'ОАЭ',
    KR: 'Южная Корея',
    KP: 'Северная Корея',
    CZ: 'Чехия',
    MK: 'Северная Македония',
    CI: 'Кот-д’Ивуар',
    DO: 'Доминиканская Республика',
    CD: 'ДР Конго',
    BA: 'Босния и Герцеговина',
};
/** «е» → «ё» в словах, где ICU пишет без неё. */
const YO_FIXES = [
    [/Соединенн/g, 'Соединённ'],
    [/Объединенн/g, 'Объединённ'],
];
/** Страна ISO-2 → «Кот-д’Ивуар»; неизвестный код — `null`. */
export function countryLabel(code) {
    const up = code.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(up))
        return null;
    const own = COUNTRY_OVERRIDES[up];
    if (own)
        return own;
    if (countryNames === undefined) {
        try {
            countryNames = new Intl.DisplayNames('ru', { type: 'region', fallback: 'none' });
        }
        catch {
            countryNames = null;
        }
    }
    const name = countryNames?.of(up);
    if (!name || name === up)
        return null;
    return YO_FIXES.reduce((s, [re, to]) => s.replace(re, to), name);
}
/** Неизвестное значение без словаря: `asia_central` → «Asia central», а не сырой код. */
function humanize(raw) {
    const t = raw.trim().replace(/[_]+/g, ' ').replace(/\s+/g, ' ');
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : raw;
}
/** Цена триала в центах строкой (`'100'`, `'499'`) — деньгами. */
const formatTrialPriceTier = (raw) => {
    if (!/^\d+$/.test(raw))
        return null;
    const minor = Number(raw);
    // По-русски, как остальные деньги раздела: «$4,99», не «$4.99».
    return money(minor);
};
/** Подпись значения признака — единственная точка, где сырой ключ становится текстом. */
export const markerValueLabel = (markerKey, rawValue) => {
    const normalized = rawValue.trim().toLowerCase();
    if (markerKey === FIRST_CYCLE_PAID_14_MARKER && normalized === NOT_YET_TOKEN)
        return NOT_YET_LABEL;
    if (NULL_TOKENS.has(normalized))
        return 'нет данных';
    if (TRUE_TOKENS.has(normalized))
        return 'да';
    if (FALSE_TOKENS.has(normalized))
        return 'нет';
    const dict = MARKER_VALUE_DICTS[markerKey];
    if (dict?.[normalized] != null)
        return dict[normalized];
    if (markerKey === 'trial_price_tier') {
        const formatted = formatTrialPriceTier(rawValue);
        if (formatted != null)
            return formatted;
    }
    if (markerKey === 'country')
        return countryLabel(rawValue) ?? 'другая страна';
    // Регион/бренд вне словаря — читаемым текстом; возрастная корзина — как есть.
    return humanize(rawValue);
};
//# sourceMappingURL=marker-labels.js.map